import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  MenuItem,
  Snackbar,
  Alert,
  InputAdornment,
  Fade,
} from "@mui/material";
import React, { useContext, useState } from "react";
import theme from "../../theme";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import {
  isServiceDeptHead,
  isServiceHead,
  isStoreHead,
} from "../../utils/roles";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import LoadingScreen from "../LoadingScreen";

// Icon
import BrokenImageRoundedIcon from "@mui/icons-material/BrokenImageRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import BorderColorRoundedIcon from "@mui/icons-material/BorderColorRounded";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";

import {
  DEPT_SCOPES,
  isAnyDept,
  ORG_DEPT_TYPES,
  scopeLabel,
  TICKET_DEPT_TYPES,
} from "../../utils/departmentTypes";

// One row per department id. A department marked as both a ticket and an org
// department (`dept_org`) arrives in both of the context's lists and is still a
// single row, so merging without this shows it twice.
const dedupeById = (rows) => {
  const byId = new Map();
  rows.forEach((row) => byId.set(row.dept_id, row));
  return [...byId.values()].sort((a, b) =>
    (a.dept_name || "").localeCompare(b.dept_name || ""),
  );
};

// Two departments of the same name clash only if they would show up on the same
// side of the app.
//
// Before the ticket/org split there was one list, so one name check. Now a
// ticket department called Sales and an org department called Sales are two
// different things and both are legitimate, so refusing the second because the
// first exists would make the split unusable.
const sharesScope = (a, b) =>
  (TICKET_DEPT_TYPES.includes(a) && TICKET_DEPT_TYPES.includes(b)) ||
  (ORG_DEPT_TYPES.includes(a) && ORG_DEPT_TYPES.includes(b));

function DepartmentMaster() {
  const contextData = useContext(AutoTaskAuthContext);
  const {
    userInfo,
    allKumuduDepartments,
    allKumuduProjects,
    allInternalDepartments,
    allInternalProjects,
    allKumuduOrgDepartments,
    allInternalOrgDepartments,
    setReloadAppContext,
    allUsers,
  } = contextData;
  const userStore_id = userInfo?.store_id;

  // This screen owns the whole department list, both kinds. The context keeps
  // ticket departments and org departments apart so that no consumer of one can
  // accidentally read the other; here they are merged back so a department of
  // either kind can be created and edited from one place.
  const ticketDepartments =
    (userStore_id === 0 ? allKumuduDepartments : allInternalDepartments) || [];
  const orgDepartments =
    (userStore_id === 0
      ? allKumuduOrgDepartments
      : allInternalOrgDepartments) || [];
  const departments = dedupeById([...ticketDepartments, ...orgDepartments]);
  const projects =
    (userStore_id === 0 ? allKumuduProjects : allInternalProjects) || [];
  const allStaff = allUsers?.filter((user) => user.status === "active");

  const [isLoading, setIsLoading] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [searchDeptOrProjectText, setSearchDeptOrProjectText] = useState("");
  const [currentForm, setCurrentForm] = useState("");
  const [overlay, setOverlay] = useState(false);
  const [deptFormData, setDeptFormData] = useState({
    departmentName: "",
    departmentHead: null,
    icon: null,
    type: "dept",
  });
  const [iconPreview, setIconPreview] = useState("");
  const [projectFormData, setProjectFormData] = useState({
    projectName: "",
    projectHead: null,
    icon: null,
    type: "project",
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editDeptOrProject, setEditDeptOrProject] = useState(null);
  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  // Close Snackbar
  const handleClose = () => {
    setSnackBarConfig((prevState) => ({ ...prevState, open: false }));
  };

  // Dropzone setup
  const { getRootProps, getInputProps, open } = useDropzone({
    accept: {
      "image/*": [],
      "image/svg+xml": [],
    },
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const blobUrl = URL.createObjectURL(file);
        setIconPreview(blobUrl);
        setDeptFormData((prev) => ({
          ...prev,
          icon: file,
        }));
      }
    },
  });

  // Filter departments / projects based on search
  const filteredDeptsOrProjects =
    searchDeptOrProjectText.trim() === ""
      ? [...departments, ...projects]
      : [
          ...departments.filter((dept) =>
            dept?.dept_name
              .toLowerCase()
              .includes(searchDeptOrProjectText?.toLowerCase())
          ),
          ...projects.filter((proj) =>
            proj?.dept_name
              .toLowerCase()
              .includes(searchDeptOrProjectText?.toLowerCase())
          ),
        ];

  // Create department / project form fields handler
  const handleFormChange = (field, value) => {
    currentForm === "dept"
      ? setDeptFormData((prev) => ({ ...prev, [field]: value }))
      : setProjectFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Show create department & create project buttons
  const handleOptionClick = (type) => {
    setIsEditMode(false);

    if (type === "department") {
      setDeptFormData({
        departmentName: "",
        departmentHead: null,
        icon: null,
        type: "dept",
      });
      setCurrentForm("dept");
      setIconPreview("");
    }

    if (type === "project") {
      setProjectFormData({
        projectName: "",
        projectHead: null,
        icon: null,
        type: "project",
      });
      setCurrentForm("project");
      setIconPreview("");
    }

    setOverlay(true);
    setShowOptions(false);
  };

  // Create department handler
  const handleCreateDeptOrProject = async () => {
    // Empty department/project name checker
    if (
      (currentForm === "dept" && deptFormData.departmentName === "") ||
      (currentForm === "project" && projectFormData.projectName === "")
    ) {
      setSnackBarConfig({
        open: true,
        message: `Enter ${
          currentForm === "dept" ? "department" : "project"
        } name`,
        severity: "error",
      });
      return;
    }

    // Duplicate department name checker, within the same scope only
    if (
      currentForm === "dept" &&
      departments.some(
        (dept) =>
          dept.dept_name?.trim().toLowerCase() ===
            deptFormData.departmentName.trim().toLowerCase() &&
          sharesScope(dept.type, deptFormData.type || "dept")
      )
    ) {
      setSnackBarConfig({
        open: true,
        message: "Department already exists",
        severity: "error",
      });
      return;
    }
    setIsLoading(true);

    const URL = `${process.env.REACT_APP_API_BASE_URL}/api/auth/createdepartment`;
    try {
      const formData = new FormData();
      formData.append(
        "name",
        currentForm === "dept"
          ? deptFormData.departmentName
          : projectFormData.projectName
      );
      formData.append("store_id", userStore_id);
      // FormData stringifies whatever it is handed, so an unset head has to go
      // over the wire as "": appending null/undefined posts the literal text
      // "null"/"undefined", which the API then reads as a users.id.
      const headValue =
        currentForm === "dept"
          ? deptFormData.departmentHead
          : projectFormData.projectHead;
      formData.append("head", headValue ?? "");
      formData.append(
        "icon",
        currentForm === "dept" ? deptFormData.icon : projectFormData.icon
      );
      // The department's scope, not the form it was filled in on: `dept`
      // (tickets only), `org_dept` (org chart only) or `dept_org` (both).
      formData.append(
        "type",
        currentForm === "dept" ? deptFormData.type || "dept" : "project",
      );

      // Debugging payload
      // for (let [key, value] of formData.entries()) {
      //   console.log(key, ":", value);
      // }

      const res = await axios.post(URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!res?.data?.success) throw res?.data;

      setDeptFormData({
        departmentName: "",
        departmentHead: null,
        icon: null,
        type: "dept",
      });
      setProjectFormData({
        projectName: "",
        projectHead: null,
        icon: null,
        type: "project",
      });
      setIconPreview("");
      setOverlay(false);
      setEditDeptOrProject(null);
      setReloadAppContext((prevState) => !prevState);
    } catch (error) {
      setEditDeptOrProject(null);
      setOverlay(false);
      setDeptFormData({
        departmentName: "",
        departmentHead: null,
        icon: null,
        type: "dept",
      });
      setProjectFormData({
        projectName: "",
        projectHead: null,
        icon: null,
        type: "project",
      });
      setSnackBarConfig({
        open: true,
        message:
          error.statusMessage ||
          `${
            currentForm === "dept" ? "Department" : "Project"
          } was not created`,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Edit department handler
  const handleEditDeptOrProject = async (currentDeptOrProject) => {
    const newDeptName = deptFormData.departmentName.trim().toLowerCase();
    if (newDeptName !== currentDeptOrProject.dept_name.trim().toLowerCase()) {
      if (
        newDeptName === currentDeptOrProject.dept_name.trim().toLowerCase() ||
        departments.some(
          (dept) =>
            dept.dept_id !== currentDeptOrProject.dept_id &&
            dept.dept_name?.trim().toLowerCase() === newDeptName &&
            sharesScope(dept.type, deptFormData.type || "dept")
        )
      ) {
        setSnackBarConfig({
          open: true,
          message: "Name already in use",
          severity: "error",
        });
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);
    const URL = `${process.env.REACT_APP_API_BASE_URL}/api/auth/updatedepartment/${currentDeptOrProject.dept_id}`;
    try {
      const formData = new FormData();
      formData.append(
        "name",
        currentForm === "dept"
          ? deptFormData.departmentName
          : projectFormData.projectName
      );
      formData.append("store_id", userStore_id);
      // FormData stringifies whatever it is handed, so an unset head has to go
      // over the wire as "": appending null/undefined posts the literal text
      // "null"/"undefined", which the API then reads as a users.id.
      const headValue =
        currentForm === "dept"
          ? deptFormData.departmentHead
          : projectFormData.projectHead;
      formData.append("head", headValue ?? "");
      formData.append(
        "icon",
        currentForm === "dept" ? deptFormData.icon : projectFormData.icon
      );
      // The department's scope, not the form it was filled in on: `dept`
      // (tickets only), `org_dept` (org chart only) or `dept_org` (both).
      formData.append(
        "type",
        currentForm === "dept" ? deptFormData.type || "dept" : "project",
      );

      // Debugging payload
      // for (let [key, value] of formData.entries()) {
      //   console.log(key, ":", value);
      // }

      const res = await axios.put(URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!res?.data?.success) throw res?.data;

      setSnackBarConfig({
        open: true,
        message: res?.data?.success?.statusMessage || "Department edited",
        severity: "success",
      });

      setDeptFormData({
        departmentName: "",
        departmentHead: null,
        icon: null,
        type: "dept",
      });
      setProjectFormData({
        projectName: "",
        projectHead: null,
        icon: null,
        type: "project",
      });
      setIconPreview("");
      setOverlay(false);
      setEditDeptOrProject(null);
      setReloadAppContext((prevState) => !prevState);
    } catch (error) {
      setEditDeptOrProject(null);
      setOverlay(false);
      setDeptFormData({
        departmentName: "",
        departmentHead: null,
        icon: null,
        type: "dept",
      });
      setProjectFormData({
        projectName: "",
        projectHead: null,
        icon: null,
        type: "project",
      });
      setSnackBarConfig({
        open: true,
        message: error.statusMessage || "Department edit failed",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // console.log(editDeptOrProject);

  // console.log(
  //   currentForm === "dept"
  //     ? deptFormData.departmentHead
  //     : projectFormData.projectHead
  // );

  // console.log(
  //   departments
  //     ?.find((dept) => dept?.dept_id === editDeptOrProject?.dept_id)
  //     ?.staff?.filter((staff) =>
  //       userStore_id === 0
  //         ? staff.role !== "servicehead"
  //         : staff.role !== "storehead"
  //     )
  // );

  // console.log(
  //   departments
  //     ?.find((dept) => dept?.dept_id === deptFormData?.dept_id)
  //     ?.staff?.find((s) => s.staff_id === deptFormData?.departmentHead)
  //     ?.staff_name
  //   // departments
  //   //   ?.find((dept) => dept?.dept_id === 18)
  //   //   ?.staff?.find((s) => s.staff_id === 587)
  //   //   ?.staff_name
  // );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 2,
        mt: 2,
        mb: 6,
      }}
    >
      {/* Loading Screen */}
      {isLoading && (
        <LoadingScreen
          loadingMessage={`Processing ${
            currentForm === "dept" ? "department" : "project"
          } data`}
        />
      )}

      {/* Store ID */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          gap: 0.5,
          width: "100%",
          border: `1px solid ${theme.lightTheme.themeCol}`,
          borderRadius: 2,
          py: 1.25,
        }}
      >
        <BusinessRoundedIcon
          sx={{ fontSize: 24, color: theme.lightTheme.themeCol }}
        />
        <Typography
          sx={{
            fontSize: 18,
            color: theme.lightTheme.themeCol,
            fontWeight: 600,
          }}
        >
          Store ID : {userInfo.store_id}
        </Typography>
      </Box>

      {/* Search and Add Department */}
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            placeholder="Department / Project name"
            variant="outlined"
            value={searchDeptOrProjectText}
            onChange={(e) => setSearchDeptOrProjectText(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon
                    sx={{ color: theme.lightTheme.placeholder }}
                  />
                </InputAdornment>
              ),
            }}
            sx={formStyle}
          />
        </Grid>
      </Grid>

      {/* Available Departments / Projects */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
          gap: 1.5,
        }}
      >
        {/* No Results Found */}
        {filteredDeptsOrProjects.length === 0 && (
          <Typography
            sx={{
              fontSize: 16,
              color: theme.lightTheme.placeholder,
              fontStyle: "italic",
            }}
          >
            No results found
          </Typography>
        )}

        {/* All Departments / Projects */}
        {filteredDeptsOrProjects.length > 0 && (
          <>
            {/* Departments */}
            <Typography
              sx={{ fontSize: 18, fontWeight: 600, textAlign: "center" }}
            >
              Departments
            </Typography>

            {/* List */}
            {filteredDeptsOrProjects
              .filter(isAnyDept)
              .map((dept, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1,
                    width: "100%",
                    minHeight: 80,
                    borderRadius: 3,
                    bgcolor: "white",
                    px: 2,
                    border: `1px solid ${theme.lightTheme.placeholder}`,
                    boxShadow: "0px 5px 7px #c9c9c9",
                  }}
                >
                  {/* Icon */}
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      bgcolor: dept?.icon ? "transparent" : "#c6f5f3ff",
                    }}
                  >
                    {dept?.icon ? (
                      <Box
                        component="img"
                        src={dept.icon}
                        alt="Department Icon"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <BrokenImageRoundedIcon
                        sx={{
                          fontSize: 28,
                          color: theme.lightTheme.placeholder,
                        }}
                      />
                    )}
                  </Box>

                  {/* Department, HOD Info & Edit Icon */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        flexDirection: "column",
                        alignSelf: "stretch",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                          {dept.dept_name}
                        </Typography>

                        {/* Where this department shows up. Without it the list
                            is two different kinds of thing looking identical,
                            and the only way to tell them apart is to open one. */}
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: 0.3,
                            textTransform: "uppercase",
                            px: 0.75,
                            py: 0.15,
                            borderRadius: 100,
                            color: "#4a4a4a",
                            bgcolor: "#ededed",
                          }}
                        >
                          {scopeLabel(dept.type) || dept.type}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 12 }}>
                        HOD :{" "}
                        <span style={{ fontWeight: 600 }}>
                          {dept.staff.find(
                            (s) => isServiceDeptHead(s?.staff_role)
                          )?.staff_name || "--"}
                        </span>
                      </Typography>
                    </Box>

                    {/* Update Department Button */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",

                        border: `1px solid ${theme.lightTheme.themeCol}`,
                        borderRadius: 100,
                        p: 1,
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditMode(true);
                        setCurrentForm("dept");
                        setEditDeptOrProject(dept);

                        const dept_head = dept?.staff?.find(
                          (s) => isServiceDeptHead(s.staff_role)
                        );

                        setDeptFormData({
                          departmentName: dept.dept_name,
                          departmentHead: dept_head?.staff_id || null,
                          // Its existing scope, so opening the form to rename a
                          // department does not silently move it off the org
                          // chart or into the ticket dropdown.
                          type: dept.type || "dept",
                          icon: dept.icon || null, // Important: Reset file input
                        });

                        setIconPreview(dept.icon || "");
                        setOverlay(true);
                      }}
                    >
                      <EditRoundedIcon sx={{ fontSize: 24 }} />
                    </Box>
                  </Box>
                </Box>
              ))}

            {/* Fallback (if no department exists with the searched name) */}
            {filteredDeptsOrProjects.filter(isAnyDept).length === 0 && (
              <Typography sx={{ fontStyle: "italic", color: "#C4C4C4" }}>
                No department found with this name
              </Typography>
            )}

            {/* =========================================================== */}

            {/* Projects */}
            <Typography
              sx={{ fontSize: 18, fontWeight: 600, textAlign: "center", mt: 2 }}
            >
              Projects
            </Typography>

            {/* List */}
            {filteredDeptsOrProjects
              .filter((proj) => proj.type === "project")
              .map((proj, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1,
                    width: "100%",
                    minHeight: 80,
                    borderRadius: 3,
                    bgcolor: "white",
                    px: 2,
                    border: `1px solid ${theme.lightTheme.placeholder}`,
                    boxShadow: "0px 5px 7px #c9c9c9",
                  }}
                >
                  {/* Department, HOD Info & Edit Icon */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        flexDirection: "column",
                        alignSelf: "stretch",
                      }}
                    >
                      <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                        {proj.dept_name}
                      </Typography>
                      <Typography sx={{ fontSize: 12 }}>
                        HOD :{" "}
                        <span style={{ fontWeight: 600 }}>
                          {proj.staff.find(
                            (s) => isServiceDeptHead(s?.staff_role)
                          )?.staff_name || "--"}
                        </span>
                      </Typography>
                    </Box>

                    {/* Update Project Button */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",

                        border: `1px solid ${theme.lightTheme.themeCol}`,
                        borderRadius: 100,
                        p: 1,
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditMode(true);
                        setCurrentForm("project");
                        setEditDeptOrProject(proj);

                        const dept_head = proj?.staff?.find(
                          (s) => isServiceDeptHead(s.staff_role)
                        );

                        setProjectFormData({
                          projectName: proj.dept_name,
                          // `projectHead`, not `departmentHead`: the save reads
                          // projectFormData.projectHead, so the wrong key left
                          // it undefined and put "undefined" on the wire.
                          projectHead: dept_head?.staff_id || null,
                          type: "project",
                          icon: proj.icon || null, // Important: Reset file input
                        });

                        setIconPreview(proj.icon || "");
                        setOverlay(true);
                      }}
                    >
                      <EditRoundedIcon sx={{ fontSize: 24 }} />
                    </Box>
                  </Box>
                </Box>
              ))}

            {/* Fallback (if no project exists with the searched name) */}
            {filteredDeptsOrProjects.filter((proj) => proj.type === "project")
              .length === 0 && (
              <Typography sx={{ fontStyle: "italic", color: "#C4C4C4" }}>
                No project found with this name
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Add New Button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          width: 120,
          p: 1,
          borderRadius: 50,
          border: "4px solid #d4ddff",
          boxShadow: 6,
          position: "fixed",
          bottom: "calc(86px + env(safe-area-inset-bottom, 44px))",
          right: "16px",
          cursor: "pointer",
          background: showOptions
            ? "linear-gradient(90deg, #c92238ff 0%, #9e0707ff 100%)"
            : theme.lightTheme.header.home,
          transition: "background 0.3s ease",
        }}
        onClick={() => setShowOptions((prevState) => !prevState)}
      >
        <Box
          sx={{
            position: "relative",
            height: "20px",
            width: "60px",
          }}
        >
          {/* New */}
          <Typography
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              fontSize: 14,
              color: "#fff",
              opacity: showOptions ? 0 : 1,
              transition: "opacity 0.3s ease",
            }}
          >
            New
          </Typography>

          {/* Collapse */}
          <Typography
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              fontSize: 14,
              color: "#fff",
              opacity: showOptions ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}
          >
            Collapse
          </Typography>
        </Box>

        {/* Icon */}
        <AddRoundedIcon
          sx={{
            fontSize: 18,
            color: "#fff",
            transition: "transform 0.3s ease",
            transform: `rotate(${showOptions ? "135deg" : "0deg"})`,
          }}
        />
      </Box>
      {/* Department / Project Option Buttons (appear only when add new button is clicked) */}
      <Fade in={showOptions}>
        <Box
          sx={{
            position: "fixed",
            bottom: "calc(86px + env(safe-area-inset-bottom, 44px) + 60px)",
            right: "16px",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            zIndex: 1000,
          }}
        >
          {/* Add New Department */}
          <Box
            sx={{
              background: "#1976d2",
              width: 120,
              p: 1,
              borderRadius: 50,
              border: "4px solid #d4ddff",
              boxShadow: 6,
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => handleOptionClick("department")}
          >
            <Typography sx={{ color: "#fff", fontSize: 14 }}>
              Department +
            </Typography>
          </Box>

          {/* Add New Project */}
          <Box
            sx={{
              background: "#4caf50",
              width: 120,
              p: 1,
              borderRadius: 50,
              border: "4px solid #d4ddff",
              boxShadow: 6,
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => handleOptionClick("project")}
          >
            <Typography sx={{ color: "#fff", fontSize: 14 }}>
              Project +
            </Typography>
          </Box>
        </Box>
      </Fade>

      {/* Overlay Form */}
      {overlay && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(8px)",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            zIndex: 100,
          }}
        >
          <Box
            sx={{
              bgcolor: "white",
              borderRadius: 4,
              p: 3,
              width: { xs: "90%", sm: "400px" },
              display: "flex",
              flexDirection: "column",
              gap: 2,
              boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
            }}
          >
            {/* Form Heading & Cancel Button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {/* Heading */}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {isEditMode
                  ? `Update ${
                      currentForm === "dept" ? "Department" : "Project"
                    }`
                  : `Create ${
                      currentForm === "dept" ? "Department" : "Project"
                    }`}
              </Typography>

              {/* Cancel Button */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  cursor: "pointer",

                  borderRadius: 100,
                  background:
                    "linear-gradient(90deg, #c92238ff 0%, #9e0707ff 100%)",
                  color: "white",
                  mt: -5,
                  mr: -2,
                }}
                onClick={() => {
                  setEditDeptOrProject(null);
                  setOverlay(false);
                  setDeptFormData({
                    departmentName: "",
                    departmentHead: null,
                    icon: null,
                    type: "dept",
                  });
                  setProjectFormData({
                    projectName: "",
                    projectHead: null,
                    type: "project",
                  });
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 20 }} />
              </Box>
            </Box>

            {/* Department Icon */}
            {currentForm === "dept" && (
              <Box
                {...getRootProps()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  alignSelf: "center",
                  width: 98,
                  height: 98,
                  border: `1px solid ${theme.lightTheme.placeholder}`,
                  borderRadius: 2.5,
                  position: "relative",
                  background: iconPreview
                    ? `url(${iconPreview}) center/cover no-repeat`
                    : "transparent",
                  cursor: iconPreview ? "default" : "pointer",
                }}
                onClick={() => {
                  if (!iconPreview) open();
                }}
              >
                {!iconPreview && (
                  <>
                    <AddPhotoAlternateRoundedIcon
                      sx={{ fontSize: 28, color: theme.lightTheme.placeholder }}
                    />
                    <Typography
                      sx={{ fontSize: 12, color: theme.lightTheme.placeholder }}
                    >
                      Upload Icon
                    </Typography>
                  </>
                )}

                {/* Edit Icon Button */}
                {iconPreview && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      borderRadius: 100,
                      bgcolor: theme.lightTheme.placeholder,
                      position: "absolute",
                      right: -18,
                      bottom: -12,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      open();
                    }}
                  >
                    <BorderColorRoundedIcon
                      sx={{ fontSize: 18, fill: "#FFF" }}
                    />
                  </Box>
                )}

                <input {...getInputProps()} />
              </Box>
            )}

            {/* Department / Project Name */}
            <TextField
              label={
                currentForm === "dept" ? "Department Name" : "Project Name"
              }
              value={
                currentForm === "dept"
                  ? deptFormData.departmentName
                  : projectFormData.projectName
              }
              onChange={(e) =>
                currentForm === "dept"
                  ? handleFormChange("departmentName", e.target.value)
                  : handleFormChange("projectName", e.target.value)
              }
              fullWidth
              sx={formStyle}
            />

            {/* Where the department shows up.

                Ticket departments and org-chart departments used to be the same
                rows, so every department invented for ticket routing appeared on
                the HR screens and vice versa. They are separate now, and this is
                where that is chosen — including "Both", which is one row on both
                sides rather than two rows to keep in step. */}
            {currentForm === "dept" && (
              <TextField
                select
                label="Shows up in"
                value={deptFormData.type || "dept"}
                onChange={(e) => handleFormChange("type", e.target.value)}
                helperText={
                  DEPT_SCOPES.find(
                    (scope) => scope.value === (deptFormData.type || "dept"),
                  )?.description
                }
                fullWidth
                sx={formStyle}
              >
                {DEPT_SCOPES.map((scope) => (
                  <MenuItem key={scope.value} value={scope.value}>
                    {scope.label}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {/* HOD / HOP */}
            <TextField
              select
              label={
                currentForm === "dept" ? "Department Head" : "Project Head"
              }
              value={
                currentForm === "dept"
                  ? deptFormData.departmentHead
                  : projectFormData.projectHead
              }
              onChange={(e) =>
                currentForm === "dept"
                  ? handleFormChange("departmentHead", e.target.value)
                  : handleFormChange("projectHead", e.target.value)
              }
              fullWidth
              sx={formStyle}
            >
              {editDeptOrProject !== null
                ? currentForm === "dept"
                  ? departments?.find(
                      (dept) => dept?.dept_id === editDeptOrProject?.dept_id
                    )?.staff.length > 0
                    ? departments
                        ?.find(
                          (dept) => dept?.dept_id === editDeptOrProject?.dept_id
                        )
                        ?.staff?.filter((staff) =>
                          userStore_id === 0
                            ? !isServiceHead(staff.role)
                            : !isStoreHead(staff.role)
                        )
                        ?.map((staff) => (
                          <MenuItem key={staff.id} value={staff.id}>
                            {staff.name}
                          </MenuItem>
                        ))
                    : allStaff
                        ?.filter((staff) =>
                          userStore_id === 0
                            ? !isServiceHead(staff.role)
                            : !isStoreHead(staff.role)
                        )
                        ?.map((staff) => (
                          <MenuItem key={staff.id} value={staff.id}>
                            {staff.name}
                          </MenuItem>
                        ))
                  : projects?.find(
                      (dept) => dept?.dept_id === editDeptOrProject?.dept_id
                    )?.staff.length > 0
                  ? projects
                      ?.find(
                        (dept) => dept?.dept_id === editDeptOrProject?.dept_id
                      )
                      ?.staff?.filter((staff) =>
                        userStore_id === 0
                          ? !isServiceHead(staff.role)
                          : !isStoreHead(staff.role)
                      )
                      ?.map((staff) => (
                        <MenuItem key={staff.id} value={staff.id}>
                          {staff.name}
                        </MenuItem>
                      ))
                  : allStaff
                      ?.filter((staff) =>
                        userStore_id === 0
                          ? !isServiceHead(staff.role)
                          : !isStoreHead(staff.role)
                      )
                      ?.map((staff) => (
                        <MenuItem key={staff.id} value={staff.id}>
                          {staff.name}
                        </MenuItem>
                      ))
                : allStaff
                    ?.filter((staff) =>
                      userStore_id === 0
                        ? !isServiceHead(staff.role)
                        : !isStoreHead(staff.role)
                    )
                    ?.map((staff) => (
                      <MenuItem key={staff.id} value={staff.id}>
                        {staff.name}
                      </MenuItem>
                    ))}
            </TextField>

            {/* Create / Edit Department / Project Button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                width: "100%",
                cursor: "pointer",

                background: "linear-gradient(90deg, #008080 0%, #4428A0 100%)",
                color: "white",
                boxShadow: `
                  inset -2px -2px 12px rgba(0,0,0,0.5),  
                  0px 4px 20px rgba(0,0,0,0.4)           
                `,
                borderRadius: 2.5,
                py: 1.25,
              }}
              onClick={
                isEditMode
                  ? () => handleEditDeptOrProject(editDeptOrProject)
                  : handleCreateDeptOrProject
              }
            >
              <Typography>{isEditMode ? "Edit" : "Create"}</Typography>
              {currentForm === "dept" ? (
                <ApartmentRoundedIcon />
              ) : (
                <AccountTreeRoundedIcon />
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          "&.MuiSnackbar-root": {
            bottom: "56px !important", // force override snackbar position
          },
        }}
      >
        {/* You can use Alert for styled messages */}
        <Alert
          onClose={handleClose}
          severity={snackBarConfig.severity}
          variant="filled"
        >
          {snackBarConfig.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const formStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    "& fieldset, & .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.borders.secondaryBorder,
      borderRadius: "12px",
    },
    "&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.borders.secondaryBorder,
    },
    "&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.borders.secondaryBorder,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.borders.secondaryBorder,
  },
  "& .MuiOutlinedInput-input::placeholder": {
    fontSize: "14px",
  },
};

export default DepartmentMaster;
